// Headquarters, the studio, and the full nail profile.
//
// Three views of one page, chosen by the query string:
//   /pages/headquarters                the whole board
//   /pages/headquarters?studio=1       the same board, only your clientele
//   /pages/headquarters?client=<id>    that person's full nail profile
//
// One page means one staff gate, and no Shopify pages to create.
//
// Nothing renders until the Worker has confirmed staff access, so the page
// being publicly reachable leaks nothing. The conversation panel mounts the
// same TNChat the customer sees with role:'staff' — one chat, two sides.
//
// The server decides; this file draws. Stage, colour group, follow-up date,
// how overdue something is and the sort order all arrive computed from
// /api/staff/roster. Four surfaces (board, tabs, studio, phone) reading the
// same fields is what keeps them from disagreeing.

(function () {
  'use strict';

  var root = document.getElementById('hq-root');
  if (!root) return;

  var state = {
    session: null,
    rows: [],
    stages: [],
    staffNames: {},
    staffId: null,
    search: '',
    sort: 'urgency',
    filter: null,
    activeId: null,
    drawer: 'orders',
    chat: null,
    timer: null,
    pfUpload: null,
    client: null
  };

  var SORTS = ['urgency', 'newest', 'name'];
  var SORT_LABEL = { urgency: 'urgency', newest: 'newest message', name: 'name' };

  var GROUP_LABEL = {
    inquiries: 'inquiries &amp; messages',
    measurements: 'nail profile measurements',
    design_workshop: 'design workshop',
    proof_processing: 'proof processing',
    size_prep: 'size &amp; prep orders',
    shipping: 'shipping notification'
  };
  var GROUP_ORDER = [
    'inquiries', 'measurements', 'design_workshop',
    'proof_processing', 'size_prep', 'shipping'
  ];

  var FINGERS = ['pinky', 'ring', 'middle', 'index', 'thumb'];

  function tn() { return window.TALISNAILZ || {}; }
  function qs(k) { return new URLSearchParams(location.search).get(k); }

  function post(path, body) {
    return fetch(tn().workerUrl + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {})
    }).then(function (r) {
      return r.json().catch(function () { return { ok: false }; })
        .then(function (d) { return { status: r.status, data: d }; });
    });
  }

  // ── Order capacity ────────────────────────────────────────────────
  // One number: how many more sets Tali will take. Set it to 0 to close the
  // shop. Reads and writes /api/staff/capacity, which is staff-gated; the
  // public /api/capacity is read-only and is what the storefront uses.
  function initCapacityControl() {
    var input = document.getElementById('hq-cap');
    var btn = document.getElementById('hq-cap-save');
    var said = document.getElementById('hq-cap-said');
    if (!input || !btn || !said) return;

    function say(msg, bad) {
      said.textContent = msg;
      said.classList.toggle('is-bad', !!bad);
      if (!bad && msg) setTimeout(function () { said.textContent = ''; }, 2500);
    }

    // No `remaining` key asks for the current value rather than setting one.
    post('/api/staff/capacity', { sessionToken: state.session.token })
      .then(function (res) {
        if (!res.data.ok) return;
        // null means the row has never been written. Leave the field empty
        // rather than showing a 0, which would read as "we are closed".
        if (res.data.remaining !== null && res.data.remaining !== undefined) {
          input.value = res.data.remaining;
        }
      })
      .catch(function () {});

    function save() {
      var raw = input.value.trim();
      if (raw === '') { say('enter a number', true); return; }

      btn.disabled = true;
      say('saving…');
      post('/api/staff/capacity', {
        sessionToken: state.session.token,
        remaining: raw
      }).then(function (res) {
        btn.disabled = false;
        if (!res.data.ok) { say(res.data.error || 'could not save', true); return; }
        input.value = res.data.remaining;
        say(res.data.remaining === 0 ? 'saved, shop is closed' : 'saved');
      }).catch(function () {
        btn.disabled = false;
        say('could not save', true);
      });
    }

    btn.addEventListener('click', save);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); save(); }
    });
  }

  // Every value that reaches innerHTML goes through this. Customer names and
  // design names are user input and one of them will eventually contain a <.
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function message(title, sub) {
    root.innerHTML = '<div class="hq__msg"><strong>' + title + '</strong><span>' + sub + '</span></div>';
  }

  function ago(sec) {
    if (!sec) return '';
    var s = Math.floor(Date.now() / 1000) - sec;
    if (s < 60) return 'just now';
    if (s < 3600) return Math.floor(s / 60) + ' mins';
    if (s < 7200) return '1 hr';
    if (s < 86400) return Math.floor(s / 3600) + ' hrs';
    if (s < 172800) return '1 day';
    return Math.floor(s / 86400) + ' days';
  }

  // "2 days late", not "2 days late (was due 08/12)". Tali's rule: when a row
  // is overdue the lateness REPLACES the date rather than joining it, because
  // the number that matters is how far behind you are.
  function lateness(sec) {
    if (sec < 3600) return Math.max(1, Math.floor(sec / 60)) + ' mins late';
    if (sec < 7200) return '1 hr late';
    if (sec < 86400) return Math.floor(sec / 3600) + ' hrs late';
    if (sec < 172800) return '1 day late';
    return Math.floor(sec / 86400) + ' days late';
  }

  function shortDate(sec) {
    if (!sec) return '';
    var d = new Date(sec * 1000);
    return ('0' + (d.getMonth() + 1)).slice(-2) + '/' + ('0' + d.getDate()).slice(-2);
  }

  function dateTime(sec) {
    if (!sec) return '';
    var d = new Date(sec * 1000);
    var h = d.getHours(), ap = h >= 12 ? 'pm' : 'am';
    h = h % 12; if (!h) h = 12;
    return shortDate(sec) + ' ' + h + ':' + ('0' + d.getMinutes()).slice(-2) + ap;
  }

  function money(cents) { return '$' + (Number(cents || 0) / 100).toFixed(2); }

  // Shape, length and jewel tone are stored in ONE canonical spelling
  // (lowercase, set by the Worker) because the builder and the product page
  // used to disagree and split the same shape in two. Capitalisation is a
  // display decision, so it happens here.
  //
  // xl / xxl go fully upper, not "Xl".
  function spec(v) {
    return String(v == null ? '' : v).split(',').map(function (part) {
      var s = part.trim();
      if (!s) return '';
      if (/^x{0,2}l$/i.test(s)) return s.toUpperCase();
      return s.charAt(0).toUpperCase() + s.slice(1);
    }).filter(Boolean).join(', ');
  }


  function initialOf(id) {
    var n = state.staffNames[id] || '';
    return n ? n.trim().charAt(0).toUpperCase() : '?';
  }

  function stageLabel(key) {
    for (var i = 0; i < state.stages.length; i++) {
      if (state.stages[i].key === key) return state.stages[i].label;
    }
    return key;
  }
  function stageGroup(key) {
    for (var i = 0; i < state.stages.length; i++) {
      if (state.stages[i].key === key) return state.stages[i].group;
    }
    return 'none';
  }

  // "Talina Bear" -> "Talina's ". Only the first word belongs in a possessive.
  // Always the curly apostrophe, even after a name ending in s, because the
  // brand writes itself "Tali's Nailz" and matching that beats switching
  // styles per person. Amalyara was checked for U+2019; it has the glyph.
  function ownerPrefix(fullName) {
    var first = String(fullName || '').trim().split(/\s+/)[0];
    return first ? esc(first) + '’s ' : '';
  }


  // ── Dialogs ─────────────────────────────────────────────────────────
  //
  // window.confirm and window.prompt are the browser's, not the brand's: grey
  // Chrome boxes with a URL at the top, in the middle of a page built out of
  // pink pills. They also block the whole tab, and prompt() is free text, which
  // is how a typo becomes a stored value nothing will ever match.
  //
  // One implementation here, exposed on window.HQ, so the client page uses the
  // same one rather than growing a second look.

  function dialog(opts) {
    return new Promise(function (resolve) {
      var wrap = document.createElement('div');
      wrap.className = 'hq__dlg';
      var choices = opts.choices || null;

      wrap.innerHTML =
        '<div class="hq__dlg-scrim"></div>' +
        '<div class="hq__dlg-box" role="dialog" aria-modal="true">' +
          '<div class="hq__dlg-title">' + esc(opts.title || '') + '</div>' +
          (opts.body ? '<div class="hq__dlg-body">' + esc(opts.body) + '</div>' : '') +
          (choices
            ? '<div class="hq__dlg-chips">' +
                choices.map(function (c) {
                  return '<button class="hq__dlg-chip" data-value="' + esc(c) + '">' + esc(c) + '</button>';
                }).join('') +
              '</div>'
            : '') +
          // Caller-supplied markup, for anything that needs real fields rather
          // than a yes/no or a row of chips. NOT escaped, so only ever pass
          // markup this file built.
          (opts.html || '') +
          '<div class="hq__dlg-actions">' +
            '<button class="hq__btn" data-act="cancel">' + esc(opts.cancel || 'never mind') + '</button>' +
            (opts.extra ? '<button class="hq__btn" data-act="extra">' + esc(opts.extra) + '</button>' : '') +
            (choices ? '' :
              '<button class="hq__mini" data-act="ok">' + esc(opts.ok || 'yes') + '</button>') +
          '</div>' +
        '</div>';

      function close(v) {
        document.removeEventListener('keydown', key);
        wrap.remove();
        resolve(v);
      }
      function key(e) { if (e.key === 'Escape') close(null); }

      wrap.addEventListener('click', function (e) {
        if (e.target.closest('.hq__dlg-scrim') || e.target.closest('[data-act="cancel"]')) return close(null);
        if (e.target.closest('[data-act="extra"]')) return close('extra');
        // onOk can read the fields out of the box before it is torn down.
        if (e.target.closest('[data-act="ok"]')) {
          return close(opts.onOk ? opts.onOk(wrap) : true);
        }
        var chip = e.target.closest('.hq__dlg-chip');
        if (chip) return close(chip.dataset.value);
      });
      document.addEventListener('keydown', key);
      document.body.appendChild(wrap);
      var first = wrap.querySelector('input, .hq__dlg-chip, [data-act="ok"]');
      if (first) first.focus();
    });
  }

  // ── Board ───────────────────────────────────────────────────────────

  function renderShell(name) {
    var studioMode = qs('studio') === '1';
    root.innerHTML =
      '<div class="hq__titlerow">' +
        '<div class="hq__title">' + ownerPrefix(name) + (studioMode ? 'studio' : 'headquarters') + '</div>' +
        // Back to the filter-by-task screen without hunting for the small
        // "all clients" link inside a panel you may have scrolled.
        '<button class="hq__home" id="hq-home" title="back to filter by task" aria-label="back to filter by task">⌂</button>' +
      '</div>' +
      '<div class="hq__topbar">' +
        '<button class="hq__sort" id="hq-sort">sort by <span class="hq__sort-arrow">↑↓</span> ' +
          '<span id="hq-sort-label">' + SORT_LABEL[state.sort] + '</span></button>' +
        '<div class="hq__searchwrap">' +
          '<input class="hq__search" id="hq-search" type="text" placeholder="search by name or email" autocomplete="off">' +
        '</div>' +
        // How many more sets you are willing to take. 0 closes the shop: the
        // sold-out banner goes up and nothing made-to-order can reach checkout.
        // Until this existed the number could only be changed with a direct
        // database write, so the cap could close the shop and nothing here
        // could reopen it.
        '<div class="hq__cap">' +
          '<label class="hq__cap-label" for="hq-cap">slots open</label>' +
          '<input class="hq__cap-input" id="hq-cap" type="number" min="0" max="999" step="1" inputmode="numeric">' +
          '<button class="hq__cap-save" id="hq-cap-save">save</button>' +
          '<span class="hq__cap-said" id="hq-cap-said" aria-live="polite"></span>' +
        '</div>' +
      '</div>' +
      '<div class="hq__layout">' +
        '<div class="hq__list" id="hq-list"></div>' +
        '<div class="hq__panel" id="hq-panel"></div>' +
      '</div>';

    document.getElementById('hq-search').addEventListener('input', function (e) {
      state.search = e.target.value;
      renderList();
    });

    initCapacityControl();

    document.getElementById('hq-home').addEventListener('click', function () {
      // Deselect the person and clear the search, but KEEP the active task
      // filter: if you were working through design workshop, going home should
      // put you back at the top of design workshop, not lose your place.
      state.activeId = null;
      state.search = '';
      var box = document.getElementById('hq-search');
      if (box) box.value = '';
      if (state.chat) { state.chat.destroy(); state.chat = null; }
      var list = document.getElementById('hq-list');
      if (list) list.classList.remove('is-hidden');
      renderList();
      renderPick();
    });

    document.getElementById('hq-sort').addEventListener('click', function () {
      state.sort = SORTS[(SORTS.indexOf(state.sort) + 1) % SORTS.length];
      document.getElementById('hq-sort-label').textContent = SORT_LABEL[state.sort];
      renderList();
    });

    document.getElementById('hq-list').addEventListener('click', function (e) {
      // The profile link is an anchor with target=_blank; letting the row
      // handler run as well would also swap the panel underneath the tab you
      // just left.
      if (e.target.closest('.hq__row-cta')) return;
      var row = e.target.closest('.hq__row');
      if (row) openClient(row.dataset.id);
    });

    renderPick();
  }

  function visibleRows() {
    var rows = state.rows.slice();

    if (qs('studio') === '1') {
      rows = rows.filter(function (r) { return (r.studio || []).indexOf(state.staffId) > -1; });
    }
    if (state.filter) {
      rows = rows.filter(function (r) { return (r.groups || []).indexOf(state.filter) > -1; });
    }
    var q = state.search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(function (r) {
        return ((r.name || '') + ' ' + (r.email || '')).toLowerCase().indexOf(q) > -1;
      });
    }

    // The server sends this order already; re-applying it here means the list
    // is stable when you switch sorts and come back, and that filtering never
    // depends on the order a response happened to arrive in.
    if (state.sort === 'urgency') {
      rows.sort(function (a, b) {
        if (a.awaitingReply !== b.awaitingReply) return a.awaitingReply ? -1 : 1;
        if (a.overdueBy !== b.overdueBy) return b.overdueBy - a.overdueBy;
        if (a.dueAt && b.dueAt && a.dueAt !== b.dueAt) return a.dueAt - b.dueAt;
        if (a.dueAt && !b.dueAt) return -1;
        if (!a.dueAt && b.dueAt) return 1;
        return (b.lastMessageAt || 0) - (a.lastMessageAt || 0);
      });
    } else if (state.sort === 'newest') {
      rows.sort(function (a, b) { return (b.lastMessageAt || 0) - (a.lastMessageAt || 0); });
    } else if (state.sort === 'name') {
      rows.sort(function (a, b) {
        return (a.name || a.email || '').localeCompare(b.name || b.email || '');
      });
    }
    return rows;
  }

  function renderList() {
    var list = document.getElementById('hq-list');
    if (!list) return;
    var rows = visibleRows();

    if (!rows.length) {
      list.innerHTML = '<div class="hq__msg"><strong>' +
        (state.search || state.filter ? 'Nobody here' : 'No customers yet') + '</strong><span>' +
        (state.search ? 'Try a different name or email.'
          : state.filter ? 'Nothing is in this stage right now.'
          : qs('studio') === '1' ? 'Add clients to your studio from their panel.'
          : 'Customers appear as soon as they sign in.') +
        '</span></div>';
      return;
    }

    list.innerHTML = rows.map(function (r) {
      var open = r.customerId === state.activeId;
      var overdue = r.overdueBy > 0;
      var order = r.stageOrderId
        ? (r.orders || []).filter(function (o) { return o.id === r.stageOrderId; })[0]
        : null;

      // The date is replaced by the lateness, not joined by it.
      var time = overdue ? lateness(r.overdueBy) : ago(r.lastMessageAt || r.dueAt);

      var detail = '';
      if (open) {
        var main, sub;
        if (order) {
          main = order.itemType === 'byo' ? 'byo set from scratch' : 'shop the collection';
          sub = esc(order.title || '') +
            (order.shape || order.length || order.jewelTone
              ? '<br>' + [order.shape, order.length, order.jewelTone].filter(Boolean).map(esc).join(' | ')
              : '');
        } else if (r.awaitingReply) {
          main = 'new message';
          sub = stageLabel(r.profileStage);
        } else {
          main = (r.orders || []).length
            ? (r.orders.length + ' order' + (r.orders.length === 1 ? '' : 's') + ' active')
            : r.ordersPlaced
              ? (r.ordersPlaced + ' order' + (r.ordersPlaced === 1 ? '' : 's') + ' placed')
              : 'no orders yet';
          sub = stageLabel(r.profileStage);
        }
        detail =
          '<div class="hq__row-detail">' +
            '<div class="hq__row-detail-main">' + main + '</div>' +
            '<div class="hq__row-detail-sub">' + sub + '</div>' +
            (r.dueAt ? '<div class="hq__row-detail-sub">follow up by ' + shortDate(r.dueAt) + '</div>' : '') +
          '</div>' +
          // Opens in a new tab, deliberately: headquarters stays exactly where
          // you left it, with the conversation still scrolled where it was.
          '<a class="hq__row-cta" href="?client=' + encodeURIComponent(r.customerId) +
            '" target="_blank" rel="noopener">view full nail profile</a>';
      }

      return '<button class="hq__row' + (open ? ' is-active' : '') + (overdue ? ' is-overdue' : '') +
        '" data-group="' + esc(r.stageGroup) + '" data-id="' + esc(r.customerId) + '">' +
        '<div class="hq__row-top">' +
          '<span class="hq__row-name">' + esc(r.name || r.email || 'Customer') + '</span>' +
          '<span class="hq__row-time' + (overdue ? ' is-overdue' : '') + '">' + esc(time) + '</span>' +
        '</div>' +
        '<div class="hq__row-stage"><span class="hq__row-v">v.' + r.version + ' ✦</span> ' +
          esc(r.stageLabel) + (r.stageOrderId && order ? ' ' + shortDate(order.placedAt) : '') +
        '</div>' + detail +
      '</button>';
    }).join('');
  }

  function renderPick() {
    var panel = document.getElementById('hq-panel');
    if (!panel) return;
    var counts = (state.counts && state.counts.groups) || {};
    panel.innerHTML =
      '<div class="hq__pick">' +
        '<div class="hq__pick-note">messages awaiting a reply show at the top of ur page :p</div>' +
        '<div class="hq__pick-h">pick a conversation</div>' +
        '<div class="hq__pick-or">or</div>' +
        '<div class="hq__pick-h">filter by task</div>' +
        '<hr class="hq__pick-rule">' +
        '<div class="hq__tabs" id="hq-tabs">' +
          GROUP_ORDER.map(function (g) {
            return '<button class="hq__tab' + (state.filter === g ? ' is-on' : '') +
              '" data-group="' + g + '">' + GROUP_LABEL[g] +
              '<span class="hq__tab-n">' + (counts[g] || 0) + '</span></button>';
          }).join('') +
        '</div>' +
      '</div>';

    document.getElementById('hq-tabs').addEventListener('click', function (e) {
      var tab = e.target.closest('.hq__tab');
      if (!tab) return;
      state.filter = state.filter === tab.dataset.group ? null : tab.dataset.group;
      renderPick();
      renderList();
    });
  }

  function rowFor(id) {
    return state.rows.filter(function (r) { return r.customerId === id; })[0] || null;
  }

  // ── Panel ───────────────────────────────────────────────────────────

  function openClient(customerId) {
    if (state.chat) { state.chat.destroy(); state.chat = null; }
    state.activeId = customerId;
    state.drawer = 'orders';
    renderList();
    renderPanel();

    var list = document.getElementById('hq-list');
    if (list && window.matchMedia('(max-width: 1023px)').matches) list.classList.add('is-hidden');
  }

  function renderPanel() {
    var panel = document.getElementById('hq-panel');
    var r = rowFor(state.activeId);
    if (!panel || !r) return renderPick();

    var mine = (r.studio || []).indexOf(state.staffId) > -1;
    var others = (r.studio || []).filter(function (s) { return s !== state.staffId; });
    var overdue = r.overdueBy > 0;

    panel.innerHTML =
      '<button class="hq__back" id="hq-back">← all clients</button>' +
      '<div class="hq__panel-head">' +
        '<div class="hq__panel-name">' + esc(r.name || r.email || 'Customer') + '</div>' +
        '<div class="hq__panel-meta' + (overdue ? ' is-overdue' : '') + '">' +
          esc(r.stageLabel) + (r.dueAt
            ? ' ✦ ' + (overdue ? lateness(r.overdueBy) : 'follow up by ' + shortDate(r.dueAt))
            : '') +
        '</div>' +
      '</div>' +
      '<div class="hq__actions">' +
        '<button class="hq__btn hq__btn--dashed" data-drawer="proof">create a new proof</button>' +
        // The whole pill IS the select, so it sits flush with the buttons either
        // side of it instead of being a pill inside a pill. The label lives in
        // the option text because a <select> can only display its option.
        '<span class="hq__btn hq__vpill-btn">' +
          '<select id="hq-vsel">' +
            [[1, 'started'], [2, 'pending'], [3, 'final']].map(function (v) {
              return '<option value="' + v[0] + '"' + (r.version === v[0] ? ' selected' : '') +
                '>nail profile status : ' + v[1] + '</option>';
            }).join('') +
          '</select>' +
        '</span>' +
        '<button class="hq__btn" data-drawer="notes">notes</button>' +
        '<button class="hq__btn hq__btn--studio' + (mine ? ' is-on' : '') + '" id="hq-studio">' +
          (mine ? '♥' : '♡') + ' clientele' +
          (others.length
            ? ' <span class="hq__initials">· also ' +
                others.map(initialOf).map(esc).join(' ') + '</span>'
            : '') +
        '</button>' +
        // Mobile only. The full board puts a stage control on every order card
        // instead, so this would be a duplicate above 1024px.
        '<button class="hq__btn hq__btn--mobile" id="hq-mstage">change stage</button>' +
        '<button class="hq__btn hq__btn--icon" data-drawer="orders" title="order status">☰</button>' +
      '</div>' +
      '<div class="hq__body" id="hq-body">' +
        '<div class="hq__chat"><div id="hq-chat"></div></div>' +
        '<div class="hq__drawer" id="hq-drawer"></div>' +
      '</div>';

    panel.querySelector('#hq-back').addEventListener('click', function () {
      state.activeId = null;
      var list = document.getElementById('hq-list');
      if (list) list.classList.remove('is-hidden');
      renderList();
      renderPick();
    });

    panel.querySelector('.hq__actions').addEventListener('click', function (e) {
      var b = e.target.closest('[data-drawer]');
      if (b) {
        state.drawer = state.drawer === b.dataset.drawer ? 'orders' : b.dataset.drawer;
        renderDrawer();
      }
    });

    panel.querySelector('#hq-studio').addEventListener('click', function () {
      post('/api/staff/studio', {
        sessionToken: state.session.token, customerId: r.customerId, on: !mine
      }).then(function () { return loadBoard(); }).then(renderPanel);
    });

    panel.querySelector('#hq-vsel').addEventListener('change', function (e) {
      setProfileVersion(r, Number(e.target.value), e.target);
    });
    panel.querySelector('#hq-mstage').addEventListener('click', function () { mobileStage(r); });

    state.chat = window.TNChat.mount(document.getElementById('hq-chat'), {
      auth: 'staff',
      role: 'staff',
      threadKey: r.threadId ? { threadId: r.threadId } : { kind: 'profile', customerId: r.customerId },
      customerName: r.name || 'them',
      placeholder: 'send a message to ' + (r.name || '').split(' ')[0] + '…',
      emptyState: '<strong>No messages yet</strong><span>Say hello — they\'ll get an email.</span>',
      // Replying is what clears the blue, so the board has to redraw the
      // moment a message goes out rather than on the next 20-second poll.
      onUnread: function () { loadBoard().then(renderList); }
    });

    renderDrawer();
  }

  function renderDrawer() {
    var host = document.getElementById('hq-drawer');
    var body = document.getElementById('hq-body');
    var r = rowFor(state.activeId);
    if (!host || !r) return;

    // The proof form is the one drawer that takes the whole panel, matching
    // the mockup where it spans the full width under the buttons.
    body.classList.toggle('is-single', state.drawer === 'proof');
    var chat = body.querySelector('.hq__chat');
    if (chat) chat.hidden = state.drawer === 'proof';
    host.hidden = false;

    document.querySelectorAll('.hq__actions [data-drawer]').forEach(function (b) {
      b.classList.toggle('is-on', b.dataset.drawer === state.drawer && state.drawer !== 'orders');
    });

    if (state.drawer === 'notes') return renderNotes(host, r);
    if (state.drawer === 'proof') return renderProofPane(host, r);
    return renderOrders(host, r);
  }

  // ── Order status cards ──────────────────────────────────────────────

  function stageOptions(scope, current) {
    return state.stages
      .filter(function (s) { return s.scope === scope; })
      // 'inquiry' is not a stage anyone sets. It is the server's name for
      // "this person wrote and nobody has replied", and it clears itself the
      // moment you answer. Offering it here was a trap: picking it stored a
      // stage that no reply could clear, pinned the row to the top forever,
      // and demoted a finished profile back to pending as a side effect.
      // It stays visible only if it is somehow already the stored value, so
      // the box never lies about what is saved.
      .filter(function (s) { return s.key !== 'inquiry' || current === 'inquiry'; })
      .map(function (s) {
        return '<option value="' + s.key + '"' + (s.key === current ? ' selected' : '') + '>' +
          esc(s.label) + '</option>';
      }).join('');
  }

  function renderOrders(host, r) {
    var cards = (r.orders || []).map(function (o) { return orderCard(r, o); }).join('');

    // Someone with no open order still has a stage — their profile one — and
    // it still needs a control, or a person who has never ordered cannot be
    // moved out of the measuring queue from here at all.
    if (!cards) cards = profileCard(r);

    host.innerHTML = cards;
    wireCards(host, r);
  }

  function profileCard(r) {
    var group = stageGroup(r.profileStage);
    return '<div class="hq__card" data-order="">' +
      '<div class="hq__card-label">order status</div>' +
      '<div class="hq__card-stage">' +
        '<span class="hq__vpill">v.' + r.version + ' ✦</span>' +
        '<select class="hq__select" data-group="' + group + '" data-stage>' +
          stageOptions('profile', r.profileStage) +
        '</select>' +
      '</div>' +
      '<div class="hq__card-label">order details</div>' +
      '<div class="hq__card-details">' +
        '<div class="hq__card-title">' +
          // Lifetime, from the server. r.orders only holds what is still
          // moving, so once an order is marked delivered this would otherwise
          // flip back to "no orders yet" for a returning customer.
          (r.ordersPlaced
            ? r.ordersPlaced + ' order' + (r.ordersPlaced === 1 ? '' : 's') + ' placed'
            : 'no orders yet') +
        '</div>' +
        '<div class="hq__card-sub">' + esc(stageLabel(r.profileStage)) + '</div>' +
      '</div>' +
      cardFoot(r.dueAt, r.overdueBy) +
      (r.profileStage === 'needs_measuring'
        ? '<button class="hq__quick" data-quick="needs_reupload">can’t measure these</button>'
        : '') +
    '</div>';
  }

  function orderCard(r, o) {
    var group = stageGroup(o.stage);
    var late = o.followUpAt && state.now > o.followUpAt ? state.now - o.followUpAt : 0;
    var quick = o.stage === 'awaiting_production'
      ? '<button class="hq__quick" data-quick="awaiting_shipping" data-order="' + esc(o.id) + '">made, ready to ship</button>'
      : '';

    return '<div class="hq__card" data-order="' + esc(o.id) + '">' +
      '<div class="hq__card-label">order status</div>' +
      '<div class="hq__card-stage">' +
        '<span class="hq__vpill">v.' + r.version + ' ✦</span>' +
        '<select class="hq__select" data-group="' + group + '" data-stage>' +
          stageOptions('order', o.stage) +
        '</select>' +
      '</div>' +
      '<div class="hq__card-label">order details</div>' +
      '<div class="hq__card-details">' +
        '<div class="hq__card-title">' +
          (o.itemType === 'byo' ? 'byo set from scratch' : 'shop the collection') +
        '</div>' +
        '<div class="hq__card-sub">' + esc(o.title || o.orderName || '') + '</div>' +
        '<div class="hq__card-sub">' +
          [o.shape, o.length, o.jewelTone].filter(Boolean).map(spec).map(esc).join(' | ') +
          (o.totalCents ? ' | ' + money(o.totalCents) : '') +
        '</div>' +
      '</div>' +
      sizesLine(o) + cardFoot(o.followUpAt, late) + quick +
    '</div>';
  }

  // Visible on every order card, at every stage, so sizes can be corrected
  // later rather than only at the one moment they are first asked for.
  function sizesLine(o) {
    var s = o.sizes;
    var has = s && ['left', 'right'].some(function (k) {
      return (s[k] || []).some(function (v) { return v != null; });
    });
    return '<button class="hq__szline" data-sizes="' + esc(o.id) + '">' +
      '<span class="hq__card-label" style="margin:0">sizes cut</span>' +
      (has
        ? '<span>' + ['left', 'right'].map(function (k) {
            return k.charAt(0).toUpperCase() + ' ' +
              s[k].map(function (v) { return v == null ? '—' : v; }).join(' ');
          }).join('  |  ') + '</span>'
        : '<span class="hq__szline-none">not recorded — tap to add</span>') +
    '</button>';
  }

  function cardFoot(dueAt, late) {
    return '<div class="hq__card-foot">' +
      '<div class="hq__card-dates">' +
        'today is ' + dateTime(Math.floor(Date.now() / 1000)) + '<br>' +
        (dueAt
          ? '<span' + (late ? ' class="is-overdue"' : '') + '>' +
              (late ? lateness(late) : 'follow up by ' + dateTime(dueAt)) + '</span>'
          : 'no follow-up needed') +
      '</div>' +
      '<button class="hq__save" data-save>save</button>' +
    '</div>';
  }

  function wireCards(host, r) {
    // The drawer ELEMENT survives a tab switch -- renderDrawer only replaces
    // its innerHTML -- so without this guard, going orders -> notes -> orders
    // stacks a second click listener and one press of Save fires twice. Same
    // bug wireProofs already carries a guard for, found by reading that one.
    //
    // The listeners close over `r`, so the current row is read from state each
    // time rather than captured, and a stale closure cannot save against
    // whoever was open three clicks ago.
    if (host.dataset.cardsWired === '1') return;
    host.dataset.cardsWired = '1';

    // Recolour the dropdown as it changes, before saving, so the chosen stage
    // looks like the stage rather than staying the old colour until save.
    host.addEventListener('change', function (e) {
      var sel = e.target.closest('[data-stage]');
      if (sel) sel.dataset.group = stageGroup(sel.value);
    });

    host.addEventListener('click', function (e) {
      // Live, not the `r` captured when this listener was attached.
      var row = rowFor(state.activeId);
      if (!row) return;

      var quick = e.target.closest('[data-quick]');
      if (quick) {
        var oid = quick.dataset.order || null;
        var ord = (row.orders || []).filter(function (o) { return o.id === oid; })[0];
        // Going to "awaiting shipping" means the set is finished, which is the
        // only moment the sizes actually cut are known. Ask, then move.
        if (quick.dataset.quick === 'awaiting_shipping' && ord) {
          return askSizes(row, ord).then(function (res) {
            if (res === null) return;   // cancelled, so do not move the stage
            saveStage(row.customerId, oid, 'awaiting_shipping', quick);
          });
        }
        return saveStage(row.customerId, oid, quick.dataset.quick, quick);
      }
      var sz = e.target.closest('[data-sizes]');
      if (sz) {
        var o2 = (row.orders || []).filter(function (o) { return o.id === sz.dataset.sizes; })[0];
        if (o2) return askSizes(row, o2).then(function (res) {
          if (res) return loadBoard().then(function () { renderList(); renderPanel(); });
        });
      }
      var save = e.target.closest('[data-save]');
      if (!save) return;
      var card = save.closest('.hq__card');
      var sel = card.querySelector('[data-stage]');
      saveStage(row.customerId, card.dataset.order || null, sel.value, save);
    });
  }

  function saveStage(customerId, orderId, stage, btn) {
    var was = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'saving…';
    return post('/api/staff/stage', {
      sessionToken: state.session.token,
      customerId: customerId,
      orderId: orderId || null,
      stage: stage
    }).then(function (res) {
      btn.disabled = false;
      if (!res.data.ok) {
        btn.textContent = was;
        window.TN && window.TN.showToast(res.data.error || 'Could not save that.');
        return;
      }
      btn.textContent = 'saved ✦';
      return loadBoard().then(function () { renderList(); renderPanel(); });
    }).catch(function () {
      btn.disabled = false;
      btn.textContent = was;
      window.TN && window.TN.showToast('Could not save that.');
    });
  }

  // Pick a version outright. This used to be a button that CYCLED, which meant
  // one stray click on a finished profile sent them back to pending and the
  // customer saw their measurements disappear. A dropdown cannot be nudged by
  // accident and always says what it is.
  //
  // v.1 and v.2 both mean "not measured"; v.3 means measured. Saving a full
  // measurement grid normally moves this on its own, so this is the override
  // for when the tags are wrong.
  function setProfileVersion(r, version, el) {
    if (version === r.version) return;
    saveStage(r.customerId, null, version === 3 ? 'complete' : 'needs_measuring', el);
  }

  // Phone only: the one control the small layout keeps, since the order cards
  // it normally lives on are hidden below 1024px.
  function mobileStage(r) {
    // Was a prompt() asking you to type a number against a numbered list. On a
    // phone, in a hurry, that is a wrong stage waiting to happen.
    dialog({
      title: 'change stage to',
      choices: state.stages.map(function (s) { return s.label; }),
      cancel: 'cancel'
    }).then(function (label) {
      if (!label) return;
      var s = state.stages.filter(function (x) { return x.label === label; })[0];
      if (!s) return;
      var orderId = s.scope === 'order' && (r.orders || []).length ? r.orders[0].id : null;
      if (s.scope === 'order' && !orderId) {
        window.TN && window.TN.showToast('That stage needs an order, and they have none open.');
        return;
      }
      saveStage(r.customerId, orderId, s.key, document.getElementById('hq-mstage'));
    });
  }


  // ── Sizes cut ───────────────────────────────────────────────────────
  //
  // The one moment the true answer exists is when the set has just been made,
  // so that is where this asks. Pressing "made, ready to ship" opens the grid
  // BEFORE the stage moves; skipping is allowed and leaves the order marked as
  // not recorded, because a blocked button on a busy afternoon just means the
  // stage stops getting used.
  //
  // Saving does two different things on purpose: it FREEZES the ten numbers
  // onto this order, which is what shipped and must never change, and it
  // updates the reusable recipe for this shape and length, which is what gets
  // prefilled next time and is meant to drift as the fit improves.

  var FINGER_LABELS = ['pinky', 'ring', 'middle', 'index', 'thumb'];

  function sizesGridHtml(sizes) {
    function row(side) {
      return '<div class="hq__sz-row">' +
        '<span class="hq__sz-side">' + side + '</span>' +
        FINGER_LABELS.map(function (f, i) {
          var v = sizes && sizes[side] && sizes[side][i] != null ? sizes[side][i] : '';
          return '<label class="hq__sz-cell"><span>' + f + '</span>' +
            '<input type="text" inputmode="decimal" data-side="' + side + '" data-i="' + i +
            '" value="' + esc(v) + '" placeholder="—"></label>';
        }).join('') +
      '</div>';
    }
    return '<div class="hq__sz">' + row('left') + row('right') + '</div>';
  }

  function readSizesGrid(box) {
    var out = { left: [null, null, null, null, null], right: [null, null, null, null, null] };
    box.querySelectorAll('.hq__sz input').forEach(function (el) {
      var raw = el.value.trim();
      var n = Number(raw);
      // Half sizes are real, so these are floats. Anything unparseable stays
      // null rather than becoming 0 -- 0 is a valid tip size and would be a
      // wrong instruction to cut, not a blank.
      out[el.dataset.side][Number(el.dataset.i)] = raw === '' || !isFinite(n) ? null : n;
    });
    return out;
  }

  /**
   * Ask for the ten sizes, prefilled from this person's recipe for that shape
   * and length when they have one. Resolves true if they were saved.
   */
  function askSizes(row, order) {
    // One extra call, on a click, to get the recipe. Not on the roster because
    // every open row would then carry recipes it never shows.
    return post('/api/staff/client', {
      sessionToken: state.session.token, customerId: row.customerId
    }).then(function (res) {
      var recipes = (res.data && res.data.recipes) || [];
      // Compared case-insensitively rather than by exact string. Orders and
      // recipes are both stored lowercase now, so this should always match --
      // but if one ever drifts, the failure mode of an exact compare is a
      // silently empty grid that looks like the customer has no saved sizes.
      // Retyping ten numbers you already had is a worse outcome than a
      // defensive toLowerCase.
      var key = function (v) { return String(v == null ? '' : v).trim().toLowerCase(); };
      var match = recipes.filter(function (r) {
        return key(r.shape) === key(order.shape) && key(r.length) === key(order.length);
      })[0];
      var label = [order.length, order.shape].filter(Boolean).map(spec).join(' ');

      return dialog({
        title: 'what sizes did you cut?',
        body: match
          ? 'Prefilled from their saved ' + label + '. Change anything that came out different.'
          : (label ? 'No saved sizes for ' + label + ' yet, so this will become their recipe.' 
                   : 'This will be saved as their recipe.'),
        html: sizesGridHtml(match ? match.sizes : null),
        ok: 'save & mark ready',
        extra: 'skip for now',
        cancel: 'cancel',
        onOk: function (box) { return readSizesGrid(box); }
      }).then(function (result) {
        if (result === null) return null;             // cancelled entirely
        if (result === 'extra') return false;         // skipped, move anyway
        if (!order.shape || !order.length) return false;
        return post('/api/staff/recipe', {
          sessionToken: state.session.token,
          customerId: row.customerId,
          shape: order.shape,
          length: order.length,
          orderId: order.id,
          sizes: result
        }).then(function () { return true; });
      });
    });
  }

  // ── Notes ───────────────────────────────────────────────────────────

  function renderNotes(host, r) {
    var order = (r.orders || [])[0] || null;
    host.innerHTML =
      '<div class="hq__notes">' +
        (order
          ? '<div><div class="hq__note-h">order notes</div>' +
              '<textarea class="hq__note" id="hq-onotes" placeholder="about this order…">' +
                esc(order.orderNotes || '') + '</textarea></div>'
          : '') +
        '<div><div class="hq__note-h">staff notes</div>' +
          '<textarea class="hq__note" id="hq-snotes" placeholder="about this client…">' +
            esc(r.staffNotes || '') + '</textarea>' +
          '<div class="hq__note-hint">saves when you click away</div></div>' +
      '</div>';

    var snotes = host.querySelector('#hq-snotes');
    snotes.addEventListener('blur', function () {
      post('/api/staff/notes', {
        sessionToken: state.session.token,
        customerId: r.customerId,
        notes: snotes.value
      }).then(function () { return loadBoard(); });
    });

    var onotes = host.querySelector('#hq-onotes');
    if (onotes) {
      onotes.addEventListener('blur', function () {
        post('/api/staff/notes', {
          sessionToken: state.session.token,
          orderId: order.id,
          notes: onotes.value
        }).then(function () { return loadBoard(); });
      });
    }
  }

  // ── Proofs ──────────────────────────────────────────────────────────

  var PROOF_STATUS = {
    draft: 'Draft — not sent',
    sent: 'Sent · waiting on customer',
    changes_requested: 'Changes requested',
    approved: 'Approved · awaiting payment',
    paid: 'Paid ✦',
    superseded: 'Superseded',
    void: 'Withdrawn'
  };
  var DEPOSIT_CENTS = 1000;

  function renderProofPane(host, r) {
    host.innerHTML = '<div class="hq__msg">Loading proofs…</div>';
    if (!r.threadId) return renderProofForm(host, r, []);
    post('/api/chat/thread', { sessionToken: state.session.token, threadId: r.threadId })
      .then(function (res) { renderProofForm(host, r, (res.data && res.data.proofs) || []); })
      .catch(function () { renderProofForm(host, r, []); });
  }

  function renderProofForm(host, r, proofs) {
    var live = proofs.filter(function (p) {
      return p.status !== 'superseded' && p.status !== 'void';
    });

    var orderOpts = (r.orders || []).map(function (o) {
      return '<option value="' + esc(o.id) + '">' + esc(o.orderName || o.title || o.id) + '</option>';
    }).join('');

    host.innerHTML =
      live.map(function (p) {
        var actions = p.status === 'draft'
          ? '<button class="hq__mini" data-send="' + esc(p.id) + '">send to customer</button>' +
            '<button class="hq__mini hq__mini--ghost" data-void="' + esc(p.id) + '">delete</button>'
          : p.status !== 'paid'
            ? '<button class="hq__mini hq__mini--ghost" data-void="' + esc(p.id) + '">withdraw</button>'
            : '';
        return '<div class="hq__proof">' +
          '<img class="hq__proof-img" src="' + esc(p.imageUrl) + '" alt="">' +
          '<div class="hq__proof-body">' +
            '<div class="hq__proof-title">' + esc(p.title) + (p.revision > 1 ? ' · v' + p.revision : '') + '</div>' +
            '<div class="hq__proof-status">' + esc(PROOF_STATUS[p.status] || p.status) + '</div>' +
            '<div class="hq__proof-price">' + money(p.totalCents) + ' total · ' + money(p.balanceCents) + ' balance</div>' +
            (actions ? '<div class="hq__proof-actions">' + actions + '</div>' : '') +
          '</div>' +
        '</div>';
      }).join('') +
      '<div class="hq__pf">' +
        '<input class="hq__pf-input" id="pf-title" type="text" placeholder="design name">' +
        '<input class="hq__pf-input" id="pf-total" type="number" min="0" step="0.01" placeholder="total price (00.00)">' +
        '<input class="hq__pf-input" id="pf-spec" type="text" placeholder="shape | length | jewel tone">' +
        '<textarea class="hq__pf-input" id="pf-desc" placeholder="description"></textarea>' +
        '<textarea class="hq__pf-input hq__pf-wide" id="pf-notes" placeholder="notes for the customer"></textarea>' +
        (orderOpts
          // Which set this proof is FOR. Without it a paid balance has no order
          // to advance, because threads stopped carrying an order id.
          ? '<select class="hq__pf-input hq__pf-wide" id="pf-order">' + orderOpts + '</select>'
          : '') +
        '<div class="hq__pf-row hq__pf-wide">' +
          '<button class="hq__mini hq__mini--ghost" id="pf-pick">upload proof ↑</button>' +
          '<span class="hq__proof-price" id="pf-name">no image yet</span>' +
          '<input type="file" id="pf-file" accept="image/*" hidden>' +
          '<button class="hq__mini" id="pf-save" style="margin-left:auto">save draft</button>' +
          '<button class="hq__mini hq__mini--ghost" id="pf-reset">reset</button>' +
        '</div>' +
        '<img class="hq__pf-preview hq__pf-wide" id="pf-preview" hidden>' +
        '<div class="hq__pf-status" id="pf-status"></div>' +
      '</div>';

    wireProofs(host, r);
  }

  function wireProofs(host, r) {
    // The container survives re-renders, so this must attach EXACTLY once.
    // Without the guard every re-render stacked another listener and one click
    // fired send once per accumulated copy.
    if (host.dataset.wired !== '1') {
      host.dataset.wired = '1';
      host.addEventListener('click', function (e) {
        var s = e.target.closest('[data-send]');
        if (s) return sendProof(s.dataset.send, s);
        var v = e.target.closest('[data-void]');
        if (v) {
          return dialog({
            title: 'Remove this proof?',
            body: 'The link the customer already has will stop working.',
            ok: 'remove it'
          }).then(function (yes) {
            if (!yes) return;
            return post('/api/staff/proof/void', {
              sessionToken: state.session.token, proofId: v.dataset.void
            }).then(function () { renderDrawer(); });
          });
        }
      });
    }

    var file = host.querySelector('#pf-file');
    host.querySelector('#pf-pick').addEventListener('click', function () { file.click(); });
    host.querySelector('#pf-reset').addEventListener('click', function () {
      state.pfUpload = null;
      renderDrawer();
    });

    file.addEventListener('change', function () {
      var f = file.files && file.files[0];
      if (!f || !window.CloudinaryUpload) return;
      var status = host.querySelector('#pf-status');
      host.querySelector('#pf-name').textContent = f.name;
      status.textContent = 'Uploading…';
      var safe = String(r.name || r.email || 'unknown').toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'unknown';
      window.CloudinaryUpload.upload(f, null, 'talisnailz/' + safe + '/proofs/' + (r.threadId || ''))
        .then(function (up) {
          state.pfUpload = up;
          status.textContent = '';
          var prev = host.querySelector('#pf-preview');
          prev.src = up.url;
          prev.hidden = false;
        })
        .catch(function () { status.textContent = 'Upload failed — try again.'; });
    });

    host.querySelector('#pf-save').addEventListener('click', function () {
      var status = host.querySelector('#pf-status');
      var title = host.querySelector('#pf-title').value.trim();
      var total = Math.round(parseFloat(host.querySelector('#pf-total').value) * 100);
      var spec = host.querySelector('#pf-spec').value.trim();
      var desc = host.querySelector('#pf-desc').value.trim();
      var notes = host.querySelector('#pf-notes').value.trim();
      var orderSel = host.querySelector('#pf-order');

      if (!title) { status.textContent = 'Give it a name.'; return; }
      if (!state.pfUpload) { status.textContent = 'Add the proof image first.'; return; }
      if (!Number.isFinite(total) || total <= 0) { status.textContent = 'Enter the total price.'; return; }
      if (total <= DEPOSIT_CENTS) { status.textContent = 'Total must be more than the $10 deposit.'; return; }
      if (!r.threadId) { status.textContent = 'Send them a message first, so there is a conversation to attach this to.'; return; }

      status.textContent = 'Saving…';
      post('/api/staff/proof/save', {
        sessionToken: state.session.token,
        threadId: r.threadId,
        orderId: orderSel ? orderSel.value : null,
        title: title,
        imageUrl: state.pfUpload.url,
        imagePublicId: state.pfUpload.publicId,
        notes: [spec, desc, notes].filter(Boolean).join('\n'),
        totalCents: total,
        depositCents: DEPOSIT_CENTS
      }).then(function (res) {
        if (!res.data.ok) { status.textContent = res.data.error || 'Could not save.'; return; }
        state.pfUpload = null;
        renderDrawer();
      }).catch(function () { status.textContent = 'Could not save.'; });
    });
  }

  function sendProof(proofId, btn) {
    dialog({
      title: 'Send this proof?',
      body: 'They get an email with a link to approve it or ask for changes.',
      ok: 'send it'
    }).then(function (yes) { if (yes) reallySendProof(proofId, btn); });
  }

  function reallySendProof(proofId, btn) {
    btn.disabled = true;
    btn.textContent = 'sending…';
    post('/api/staff/proof/send', { sessionToken: state.session.token, proofId: proofId })
      .then(function (res) {
        if (!res.data.ok) {
          btn.disabled = false;
          btn.textContent = 'send to customer';
          window.TN && window.TN.showToast(res.data.error || 'Could not send.');
          return;
        }
        if (state.chat) state.chat.refresh();
        return loadBoard().then(function () { renderList(); renderDrawer(); });
      })
      .catch(function () { btn.disabled = false; btn.textContent = 'send to customer'; });
  }

  // ── Data ────────────────────────────────────────────────────────────

  function loadBoard() {
    return post('/api/staff/roster', { sessionToken: state.session.token })
      .then(function (res) {
        if (res.status === 401) return boot(true);
        if (!res.data.ok) return;
        state.rows = res.data.rows || [];
        state.counts = res.data.counts || {};
        state.stages = res.data.stages || [];
        state.staffNames = res.data.staffNames || {};
        state.staffId = res.data.staffId;
        state.now = res.data.now;
      }).catch(function () {});
  }

  function startPolling() {
    clearInterval(state.timer);
    state.timer = setInterval(function () {
      if (document.visibilityState !== 'visible') return;
      loadBoard().then(function () {
        renderList();
        // Redrawing the panel would blow away a half-typed note or proof, so
        // only the list refreshes on a poll. The panel redraws when you act.
        if (!state.activeId) renderPick();
      });
    }, 20000);
  }

  // ── Boot ────────────────────────────────────────────────────────────

  function boot(force) {
    var t = tn();
    if (!t.workerUrl) { message('Not configured', 'The messaging service isn\'t set up.'); return; }
    if (!t.customerId || !t.authSig) {
      message('Sign in required',
        'This page is for staff. <a href="/customer_authentication/login?return_to=' +
        encodeURIComponent(location.pathname + location.search) + '">Sign in</a>.');
      return;
    }
    if (force) { try { sessionStorage.removeItem('tn_staff_sess'); } catch (e) {} }

    return post('/api/staff/session', { customerId: t.customerId, ts: t.authTs, sig: t.authSig })
      .then(function (res) {
        if (!res.data.ok) {
          // Same wording whatever the reason — never confirm to a prober
          // whether an account is on the allowlist.
          message('Not available', 'This page isn\'t available on your account.');
          return;
        }
        state.session = { token: res.data.sessionToken, expiresAt: res.data.expiresAt };

        var clientId = qs('client');
        if (clientId) return window.HQClient.render(root, state, clientId);

        renderShell(res.data.name);
        return loadBoard().then(function () {
          renderList();
          renderPick();
          startPolling();
        });
      })
      .catch(function () { message('Couldn\'t load', 'Refresh the page to try again.'); });
  }

  window.HQ = { post: post, esc: esc, state: state, ago: ago, shortDate: shortDate, money: money, dialog: dialog };
  document.addEventListener('DOMContentLoaded', function () { boot(false); });
})();
